if (!customElements.get("bundle-builder")) {
  customElements.define(
    "bundle-builder",
    class BundleBuilder extends HTMLElement {
      constructor() {
        super();

        this.cart = document.querySelector("cart-drawer");
        this.submitButton = this.querySelector("[data-bundle-submit]");
        this.errorContainer = this.querySelector("[data-bundle-error]");
        this.progressContainer = this.querySelector("[data-bundle-progress]");
        this.meterFilled = this.querySelector("[data-bundle-meter-filled]");
        this.meterElement = this.querySelector("[data-bundle-meter]");
        this.tierNodes = Array.from(
          this.querySelectorAll("[data-bundle-tier]"),
        );
        this.ctaLabel = this.querySelector("[data-bundle-cta-label]");
        this.ctaTotal = this.querySelector("[data-bundle-cta-total]");
        this.buttonBaseLabel =
          this.ctaLabel?.textContent.trim() || "Add bundle to cart";
        this.moneyFormat = this.dataset.moneyFormat || null;
        this.moneySymbol = this.dataset.moneySymbol || "";

        this.bestDealBadge = this.querySelector("[data-bundle-best-deal]");

        this.items = Array.from(
          this.querySelectorAll("[data-bundle-item]"),
        ).map((node) => ({
          node,
          variantId: node.dataset.variantId,
          productId: node.dataset.productId,
          priceCents: parseInt(node.dataset.variantPrice || "0", 10) || 0,
          input: node.querySelector("[data-bundle-qty-input]"),
          minus: node.querySelector("[data-bundle-qty-minus]"),
          plus: node.querySelector("[data-bundle-qty-plus]"),
          addButton: node.querySelector("[data-bundle-add-btn]"),
          qtyContainer: node.querySelector("[data-bundle-qty-container]"),
        }));

        this.tiers = this._readTiers();

        // Set tier count on the meter element for CSS positioning
        if (this.meterElement) {
          this.meterElement.setAttribute('data-tier-count', this.tiers.length);
        }

        this.onSubmit = this.onSubmit.bind(this);
      }

      connectedCallback() {
        if (this.submitButton) {
          this.submitButton.addEventListener("click", this.onSubmit);
        }

        this.items.forEach((item) => {
          if (!item.input) return;

          const updateQty = (delta) => {
            const current = parseInt(item.input.value || "0", 10) || 0;
            let next = current + delta;
            if (next < 0) next = 0;
            item.input.value = next.toString();
            this._updateItemSelectedState(item);
            this._updateItemVisibility(item);
            this.updateSummary();
          };

          // Handle "Add +" button click
          if (item.addButton) {
            item.addButton.addEventListener("click", () => {
              item.input.value = "1";
              this._updateItemSelectedState(item);
              this._updateItemVisibility(item);
              this.updateSummary();
            });
          }

          if (item.minus) {
            item.minus.addEventListener("click", () => updateQty(-1));
          }
          if (item.plus) {
            item.plus.addEventListener("click", () => updateQty(1));
          }
          item.input.addEventListener("change", () => {
            let value = parseInt(item.input.value || "0", 10) || 0;
            if (value < 0) value = 0;
            item.input.value = value.toString();
            this._updateItemSelectedState(item);
            this._updateItemVisibility(item);
            this.updateSummary();
          });

          // Initialize visibility
          this._updateItemVisibility(item);
        });

        this.updateSummary();
      }

      disconnectedCallback() {
        if (this.submitButton) {
          this.submitButton.removeEventListener("click", this.onSubmit);
        }
      }

      _readTiers() {
        const tierCount = parseInt(this.dataset.tierCount || "4", 10);
        const tiers = [
          { id: "tier-1", minQty: 1, discount: 0 }
        ];

        // Dynamically read tiers 2-6 based on tierCount
        for (let i = 2; i <= tierCount && i <= 6; i++) {
          const qtyKey = `tier${i}Qty`;
          const discountKey = `tier${i}Discount`;
          
          const qty = parseInt(this.dataset[qtyKey] || String(i), 10);
          const discount = parseFloat(this.dataset[discountKey] || "0");
          
          tiers.push({
            id: `tier-${i}`,
            minQty: qty,
            discount: discount
          });
        }

        return tiers;
      }

      _getTotalQuantity() {
        return this.items.reduce((total, item) => {
          const qty = parseInt(item.input?.value || "0", 10) || 0;
          return total + qty;
        }, 0);
      }

      _getSelectedItems() {
        return this.items
          .map((item) => {
            const quantity = parseInt(item.input?.value || "0", 10) || 0;
            return {
              variantId: item.variantId,
              quantity,
            };
          })
          .filter((entry) => entry.quantity > 0);
      }

      _getTotalPriceCents() {
        return this.items.reduce((total, item) => {
          const qty = parseInt(item.input?.value || "0", 10) || 0;
          return total + item.priceCents * qty;
        }, 0);
      }

      _updateItemSelectedState(item) {
        const qty = parseInt(item.input?.value || "0", 10) || 0;
        item.node.classList.toggle("wt-bundle-builder__item--selected", qty > 0);
      }

      _updateItemVisibility(item) {
        const qty = parseInt(item.input?.value || "0", 10) || 0;
        const hasQuantity = qty > 0;
        
        if (item.addButton) {
          item.addButton.style.display = hasQuantity ? "none" : "inline-flex";
        }
        if (item.qtyContainer) {
          item.qtyContainer.style.display = hasQuantity ? "inline-flex" : "none";
        }
      }

      _getCurrentAndNextTier(totalQty) {
        let current = this.tiers[0];
        let next = null;

        for (let i = 0; i < this.tiers.length; i += 1) {
          const tier = this.tiers[i];
          if (totalQty >= tier.minQty) {
            current = tier;
          } else {
            next = tier;
            break;
          }
        }

        return { current, next };
      }

      updateSummary() {
        const totalQty = this._getTotalQuantity();
        const { current, next } = this._getCurrentAndNextTier(totalQty);
        const totalPriceCentsRaw = this._getTotalPriceCents();
        let displayPriceCents = totalPriceCentsRaw;

        if (totalQty && current && current.discount) {
          displayPriceCents = Math.round(
            totalPriceCentsRaw * (1 - current.discount / 100),
          );
        }

        this.tierNodes.forEach((node) => {
          const tierId = node.dataset.bundleTier;
          const tier = this.tiers.find((t) => t.id === tierId);
          if (!tier) return;

          const isReached = totalQty >= tier.minQty;
          const isCurrent =
            isReached && (!next || tier.minQty === current.minQty);

          node.classList.toggle(
            "wt-bundle-meter__item--reached",
            !!isReached,
          );
          node.classList.toggle(
            "wt-bundle-meter__item--current",
            !!isCurrent,
          );
        });

        if (this.meterFilled) {
          if (!totalQty) {
            this.meterFilled.style.width = "0%";
          } else {
            const currentIndex = this.tiers.findIndex(
              (tier) => tier.id === current.id,
            );
            const steps = this.tiers.length;
            const ratio =
              currentIndex >= 0 ? (currentIndex + 1) / steps : 0;
            this.meterFilled.style.width = `${Math.min(
              Math.max(ratio, 0),
              1,
            ) * 100}%`;
          }
        }

        if (this.bestDealBadge) {
          const lastTier = this.tiers[this.tiers.length - 1];
          const isBestDeal =
            totalQty && current && lastTier && current.id === lastTier.id;
          this.bestDealBadge.classList.toggle("is-active", !!isBestDeal);
        }

        if (this.progressContainer) {
          if (!totalQty) {
            this.progressContainer.textContent =
              this.dataset.emptyMessage ||
              "Pick your favourite teas to start building your bundle.";
          } else if (next) {
            const remaining = next.minQty - totalQty;
            const percent = next.discount;
            const itemNameSingular = this.dataset.itemNameSingular || "tea";
            const itemNamePlural = this.dataset.itemNamePlural || "teas";
            const itemName = remaining === 1 ? itemNameSingular : itemNamePlural;
            
            const template = this.dataset.progressMessageTemplate || 
              "Add {remaining} more {item} to unlock {percent}% off.";
            
            this.progressContainer.textContent = template
              .replace(/{remaining}/g, remaining)
              .replace(/{item}/g, itemName)
              .replace(/{percent}/g, percent);
          } else {
            this.progressContainer.textContent =
              this.dataset.maxedMessage ||
              "Nice choice. You’ve unlocked the best bundle price.";
          }
        }

        if (this.ctaTotal) {
          if (!totalQty || !displayPriceCents) {
            this.ctaTotal.textContent = "";
          } else {
            this.ctaTotal.textContent = ` — ${this._formatMoney(
              displayPriceCents,
            )}`;
          }
        }
      }

      _formatMoney(cents) {
        if (!cents) return "";

        try {
          if (
            window.Shopify &&
            typeof window.Shopify.formatMoney === "function"
          ) {
            return window.Shopify.formatMoney(
              cents,
              this.moneyFormat || undefined,
            );
          }
        } catch (e) {
          console.error(e);
        }

        const value = (cents / 100).toFixed(2);
        const integer = value.replace(/\.\d+$/, "");
        let format = this.moneyFormat || "{{amount}}";
        format = format.replace(/{{\s*amount_no_decimals\s*}}/g, integer);
        format = format.replace(/{{\s*amount\s*}}/g, value);
        return format;
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;
        this.submitButton.toggleAttribute("disabled", isLoading);
        this.submitButton.classList.toggle("loading", isLoading);
      }

      setError(message) {
        if (this.errorContainer) {
          this.errorContainer.textContent = message || "";
        }
      }

      clearError() {
        this.setError("");
      }

      onSubmit(evt) {
        evt.preventDefault();
        this.clearError();

        const items = this._getSelectedItems();
        if (!items.length) {
          this.setError(
            this.dataset.errorEmpty || "Please select at least one tea for your bundle.",
          );
          return;
        }

        this.setLoading(true);

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData();

        items.forEach((item, index) => {
          formData.append(`items[${index}][id]`, item.variantId);
          formData.append(`items[${index}][quantity]`, item.quantity);
        });

        if (this.cart && typeof this.cart.getSectionsToRender === "function") {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id),
          );
        } else {
          formData.append("sections", []);
        }

        formData.append("sections_url", window.location.pathname);
        config.body = formData;

        fetch(window.routes.cart_add_url, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "bundle-builder",
                errors: response.errors || response.description,
                message: response.message,
              });

              this.setError(
                response.message ||
                  response.description ||
                  window.cartStrings?.error ||
                  "Something went wrong while adding your bundle.",
              );
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "bundle-builder",
              cartData: response,
            });

            const isClosedCart = !document.body.classList.contains(
              "page-overlay-cart-on",
            );

            this.cart.renderContents(response, isClosedCart);
          })
          .catch((e) => {
            console.error(e);
            this.setError(
              window.cartStrings?.error ||
                "Something went wrong while adding your bundle.",
            );
          })
          .finally(() => {
            this.setLoading(false);
          });
      }
    },
  );
}



